import { useCallback, useEffect, useState } from "react";
import { Alert, Avatar, Box, CircularProgress, MenuItem, TextField, Typography } from "@mui/material";
import { Instagram } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import CustomTabs from "../../common/custom-tabs";
import AppChip from "../../common/app-chip";
import StatTile from "../../common/stat-tile";
import ToggleSwitch from "../../common/toggle-switch";
import { INSTAGRAM_ACCOUNTS, INSTAGRAM_ACCOUNT_DETAIL } from "../../config/urls";
import InstagramConnectWizard from "./InstagramConnectWizard";
import InstagramBanners from "./InstagramBanners";
import { BusinessHoursTab, IceBreakersTab, RoutingTab } from "./InstagramAccountSettings";
import { Card, STATUS_CHIP, Section, errorMessage, formatDate, useInstagramApi } from "./shared";

// Phase 1: connect Instagram accounts (wizard), manage them (Overview), Chats live in Conversation › Instagram.
// Shown inside Channel Onboarding's Instagram card (account list) and Instagram Flow › Manage account (manage).

const AUDIT_LABEL = {
  "instagram.connected": "Connected",
  "instagram.reconnected": "Reconnected",
  "instagram.disconnected": "Disconnected",
  "instagram.token_refreshed": "Token refreshed",
  "instagram.token_failed": "Token refresh failed",
  "bot.paused": "Bot paused",
  "bot.resumed": "Bot resumed",
  "icebreakers.published": "Ice breakers published",
  "instagram.settings_updated": "Settings updated",
  "rule.created": "Automation created",
  "rule.updated": "Automation updated",
  "rule.toggled": "Automation turned on/off",
  "rule.deleted": "Automation deleted",
  "comment.public_reply": "Replied to a comment",
  "comment.hidden": "Comments hidden",
  "comment.unhidden": "Comments unhidden",
};

const DETAIL_TABS = [
  { value: "overview", label: "Overview" },
  { value: "ice_breakers", label: "Ice breakers" },
  { value: "hours", label: "Business hours" },
  { value: "routing", label: "Routing" },
];

// `manage` renders only the account Manage screen (Instagram Flow › Manage account).
// Otherwise it renders the account list for Channel Onboarding's Instagram card; `onLoad` gets every accounts
// response so the card's status pill stays in step (pass a stable function, e.g. a state setter).
const InstagramConnectChat = ({ manage = false, onLoad }) => {
  const { request, notify, toast } = useInstagramApi();
  const [data, setData] = useState(null); // accounts list response
  const [detail, setDetail] = useState(null); // account in the Manage drawer
  const [detailTab, setDetailTab] = useState("overview");
  const [wizard, setWizard] = useState(null); // { reconnect?: account }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await request("get", INSTAGRAM_ACCOUNTS);
      setData(res);
      onLoad?.(res);
    } catch (error) {
      notify(errorMessage(error, "Could not load Instagram accounts."), "error");
      setData({ accounts: [], used: 0, limit: 0, role: "" });
    }
  }, [request, notify, onLoad]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (manage && data?.accounts.length && !detail) openDetail(data.accounts[0]);
  }, [manage, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (account) => {
    if (detail?.id !== account.id) setDetailTab("overview");
    setDetail({ ...account, loading: true });
    try {
      setDetail(await request("get", INSTAGRAM_ACCOUNT_DETAIL(account.id)));
    } catch (error) {
      notify(errorMessage(error, "Could not load the account."), "error");
    }
  };

  const accountAction = async (method, url, body, success) => {
    setBusy(true);
    try {
      const updated = await request(method, url, body);
      if (detail) setDetail((d) => ({ ...d, ...updated }));
      if (success) notify(success);
      load();
    } catch (error) {
      notify(errorMessage(error, "Instagram request failed."), "error");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = (account) => {
    if (!window.confirm(`Automations on @${account.username} will stop. Chat history stays in BotBridge. Disconnect?`)) return;
    accountAction("delete", INSTAGRAM_ACCOUNT_DETAIL(account.id), undefined, `@${account.username} disconnected.`);
  };

  const closeWizard = (changed) => {
    setWizard(null);
    if (changed) {
      load();
      if (detail) openDetail(detail);
    }
  };

  if (!data) return <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>;

  const isAdmin = data.role === "admin";
  const canManage = isAdmin || data.role === "manager";
  const atLimit = data.used >= data.limit;

  const renderAccounts = () => (
    <>
      <InstagramBanners data={data} onChange={load} />
      {/* the Channel Onboarding card above already introduces Instagram */}
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Typography variant="body2" color="text.secondary" flex={1}>
          {data.used} of {data.limit} account{data.limit === 1 ? "" : "s"} connected. Chats show up in Conversation › Instagram.
        </Typography>
        {isAdmin && data.accounts.length > 0 && (
          <ButtonOne variant="outlined" startIcon={<Instagram />} title="Connect another account"
            disabled={atLimit || !data.app_configured} onClick={() => setWizard({})} />
        )}
      </Box>
      {isAdmin && atLimit && (
        <Alert severity="info" sx={{ mb: 2 }}>Your plan's Instagram account limit is reached. Upgrade to connect more accounts.</Alert>
      )}
      {isAdmin && !data.app_configured && (
        <Alert severity="warning" sx={{ mb: 2 }}>Instagram is not set up on the server yet (IG_APP_ID / IG_APP_SECRET). Contact BotBridge support.</Alert>
      )}

      {data.accounts.map((a) => {
        const chip = STATUS_CHIP[a.status];
        return (
          <Card key={a.id} sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", opacity: a.status === "DISCONNECTED" ? 0.7 : 1 }}>
            <Avatar src={a.profile_picture_url} sx={{ width: 44, height: 44 }}><Instagram /></Avatar>
            <Box flex={1} minWidth={200}>
              <Typography fontWeight={700}>@{a.username}</Typography>
              <Typography variant="body2" color="text.secondary">Last event: {formatDate(a.last_event_at)}</Typography>
            </Box>
            {chip && <AppChip label={chip.label} color={chip.color} />}
            {a.quiet_warning && a.status === "CONNECTED" && <AppChip label="No events for 72 h" color="warning" variant="outlined" />}
            {isAdmin && <ButtonOne variant="outlined" title="Reconnect" onClick={() => setWizard({ reconnect: a })} />}
            {isAdmin && a.status !== "DISCONNECTED" && (
              <ButtonOne variant="outlined" title="Disconnect" disabled={busy} onClick={() => disconnect(a)}
                sx={{ color: "#d32f2f", borderColor: "#d32f2f !important" }} />
            )}
          </Card>
        );
      })}
    </>
  );

  const renderDetail = () => {
    if (detail.loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress size={28} /></Box>;
    const connected = detail.status !== "DISCONNECTED";
    const onSaved = (updated) => setDetail((d) => ({ ...d, ...updated }));
    return (
      <Box>
        <TextField select size="small" label="Account" value={detail.id} sx={{ minWidth: 200, mb: 3 }}
          onChange={(e) => openDetail(data.accounts.find((a) => a.id === e.target.value))}>
          {data.accounts.map((a) => <MenuItem key={a.id} value={a.id}>@{a.username}</MenuItem>)}
        </TextField>

        {connected && (
          <CustomTabs value={detailTab} onChange={(_, value) => setDetailTab(value)} tabs={DETAIL_TABS}
            sx={{ mb: 3, maxWidth: 720 }} />
        )}
        {connected && detailTab === "ice_breakers" && (
          <IceBreakersTab account={detail} request={request} notify={notify} canManage={canManage} />
        )}
        {connected && detailTab === "hours" && (
          <BusinessHoursTab key={detail.id} account={detail} request={request} notify={notify} canManage={canManage}
            onSaved={onSaved} />
        )}
        {connected && detailTab === "routing" && (
          <RoutingTab key={detail.id} account={detail} request={request} notify={notify} canManage={canManage}
            onSaved={onSaved} />
        )}
        {(!connected || detailTab === "overview") && renderOverview()}
      </Box>
    );
  };

  const renderOverview = () => {
    const connected = detail.status !== "DISCONNECTED";
    const volume = detail.volume_24h || {};
    return (
      <Box display="flex" flexDirection="column" gap={3}>
        <Box display="flex" flexDirection="column" gap={3} minWidth={0}>
          <Section title="Last 24 hours" hint="Messages on this account.">
            <Box display="flex" gap={2} flexWrap="wrap">
              <StatTile value={volume.inbound ?? 0} label="Received" />
              <StatTile value={volume.outbound ?? 0} label="Sent" />
              <StatTile value={volume.failed ?? 0} label="Failed" valueColor={volume.failed ? "#d32f2f" : undefined} />
            </Box>
          </Section>

          {connected && (
            <Section
              title="Bot replies"
              hint="Turn off to stop every automation on this account. Agents can still reply in Conversation › Instagram."
              action={
                <ToggleSwitch
                  checked={detail.bot_enabled}
                  onChange={() => canManage && accountAction("patch", INSTAGRAM_ACCOUNT_DETAIL(detail.id), { bot_enabled: !detail.bot_enabled })}
                />
              }
            />
          )}

          <Section title="Connection" hint="Health of the link between Instagram and BotBridge.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} rowGap={2} columnGap={3}>
              {[
                ["Webhooks", detail.webhook_subscribed ? "Subscribed" : "Not subscribed"],
                ["Token expires", formatDate(detail.token_expires_at)],
                ["Last event", formatDate(detail.last_event_at)],
                ["Connected", formatDate(detail.connected_at)],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Box>
              ))}
            </Box>
            {detail.quiet_warning && connected && (
              <Alert severity="warning">No events for 72 hours. If this account normally gets messages, run Test connection.</Alert>
            )}
            {detail.last_error && (
              <Alert severity="error">Last error ({formatDate(detail.last_error_at)}): {detail.last_error}</Alert>
            )}
          </Section>
        </Box>

        <Section title="Activity log" hint="Who changed what on this account.">
          {(detail.audit || []).length === 0 && <Typography variant="body2" color="text.secondary">No events yet.</Typography>}
          <Box display="flex" flexDirection="column" sx={{ mt: -1 }}>
            {(detail.audit || []).map((e) => (
              <Box key={e.id} display="flex" justifyContent="space-between" gap={2} py={1.25} borderBottom="1px solid #f2f4f5">
                <Box minWidth={0}>
                  <Typography variant="body2" fontWeight={600}>
                    {AUDIT_LABEL[e.action] || e.action}
                    {e.data?.reason && e.data.reason !== "tenant" ? ` (${e.data.reason.replace("_", " ")})` : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {e.actor_name}{e.data?.rule ? ` · ${e.data.rule}` : ""}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" flexShrink={0}>{formatDate(e.created_at)}</Typography>
              </Box>
            ))}
          </Box>
        </Section>
      </Box>
    );
  };

  return (
    <Box>
      {manage ? (
        detail ? renderDetail() : data.accounts.length ? <CircularProgress size={28} /> : (
          <Alert severity="info">
            No Instagram account is connected. Connect one in Channel Onboarding.
          </Alert>
        )
      ) : renderAccounts()}

      <InstagramConnectWizard
        open={Boolean(wizard)}
        reconnect={wizard?.reconnect}
        envTokenAvailable={data.env_token_available}
        request={request}
        onClose={closeWizard}
      />
      {toast}
    </Box>
  );
};

export default InstagramConnectChat;
